import { computed, getCurrentInstance, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import randomUUID from '@/utils/uuid'
import workflowApi from '@/api/workflowApi'
import { scrollToEvent } from '@/utils/scroll'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { formatCardKey } from '@/ai/utils/helpers'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { convertWorkflowToSwaggerYaml } from '@/codegen/utils'
import { isFileRelevantForAnalysis } from '@/utils/fileContent'
import { generateSortEvents, generateWorkflowFromCode, generateEntitiesExampleData, generateFileTagging } from '@/ai'

const useGenerateWorkflow = () => {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  const { proxy } = getCurrentInstance()
  const workflow = computed(() => store.state.workflow.workflow)
  const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
  const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
  const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
  const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])

  const addEvent = async (eventFromLLM) => {
    const oldVersion = workflow.value.version
    const shape = { description: eventFromLLM.description }
    const options = { laneName: eventFromLLM.role, createNewLaneIfNoLaneId: true }

    await nextTick()
    const selectedEventId = route.params.eventId && svgGraph.value[route.params.eventId]?.id
    const parentId = options.parentEventId || selectedEventId || 'start'
    if (parentId !== 'start' && workflow.value?.eventsJson?.findIndex((event) => event.id === parentId) === -1) {
      return
    }
    shape.parents = [parentId]
    const newEventId = await workflowApi.addEventToWorkflow(
      shape,
      options,
      svgLanes.value,
      svgGraph.value,
      svgGroups.value,
      workflow.value
    )
    await updateRouteWithEventId(newEventId)
    await ensureNextVersion(oldVersion)
  }

  const addCard = async (cardFromLLM) => {
    const event = getEventFromEventObj(cardFromLLM)

    if (!event) {
      return
    }

    for (let i = 0; i < requirementTypes.value?.length; i++) {
      if (requirementTypes.value[i].generateWithAI) {
        const requirementType = formatCardKey(requirementTypes.value[i].title)

        if (!cardFromLLM[requirementType]) {
          continue
        }

        if (!Array.isArray(cardFromLLM[requirementType])) {
          cardFromLLM[requirementType] = [cardFromLLM[requirementType]]
        }

        for (const cardText of cardFromLLM[requirementType]) {
          const oldVersion = workflow.value.version
          await updateRouteWithEventId(event.id)

          const args = { cardTypeName: requirementTypes.value[i].title, cardText, eventId: event.id, cardinality: 'one-to-many' }

          const index = requirementTypes.value.findIndex((type) => type.title === args.cardTypeName)
          if (index >= 0) {
            const cardTypeId = requirementTypes.value[index].id
            const eventId = args.eventId || route.params.eventId
            await workflowApi.createRequirementJson(route.params.id, eventId, {
              cardTypeId,
              description: args.cardText,
              cardinality: args.cardinality
            })
            await ensureNextVersion(oldVersion)
          }
        }
      }
    }
  }

  const addWriteModel = async (modelFromLLM) => {
    const oldVersion = workflow.value.version
    const event = getEventFromEventObj(modelFromLLM)

    if (!event) {
      return
    }

    const commandCard = cardHelpers.methods.getCommandCard(event)

    if (modelFromLLM.dataFields?.length > 0) {
      await updateRouteWithEventId(event.id)

      const { dataFields, description, role, type, cardId } = modelFromLLM
      const name = description + ' by ' + role
      const newDataFields = []
      for (const field of dataFields) {
        newDataFields.push({
          name: field.fieldName,
          cardinality: field.cardinality,
          tags: field.tags,
          exampleData: field.exampleData
        })
      }
      const eventId = event.id || route.params.eventId
      const eventData = workflow.value?.eventsJson?.find((wt) => wt.id === eventId)
      const existingDataModels = eventData?.dataModels || []

      const dataModels = []
      if (existingDataModels) dataModels.push(...existingDataModels)
      dataModels.push({
        name,
        type: type || 'Event Story',
        dataFields: newDataFields,
        cardId: cardId || commandCard?.id || null
      })
      await workflowApi.updateDataModels({
        eventId,
        dataModelsOnEvent: dataModels,
        workflow: workflow.value
      })
      await ensureNextVersion(oldVersion)
    }
  }

  const addReadModel = async (modelFromLLM) => {
    const oldVersion = workflow.value.version
    const event = getEventFromEventObj(modelFromLLM)

    if (!event) {
      return
    }

    const readModelCard = event ? cardHelpers.methods.getQueryCards(event)[0] : null

    if (modelFromLLM.readModelDataFields?.length > 0) {
      await updateRouteWithEventId(event.id)

      const { readModelDataFields, type, cardId } = modelFromLLM

      const name = 'Read Model'
      const newDataFields = []
      for (const example of readModelDataFields) {
        newDataFields.push({
          name: example.fieldName,
          cardinality: example.cardinality,
          exampleData: example.exampleData
        })
      }
      const eventId = event.id || route.params.eventId
      const existingDataModels = event?.dataModels || []

      const dataModels = []
      if (existingDataModels) {
        dataModels.push(...existingDataModels)
      }
      dataModels.push({
        name,
        type: type || 'Read Model',
        dataFields: newDataFields,
        cardId: cardId || readModelCard?.id || null
      })
      await workflowApi.updateDataModels({
        eventId,
        dataModelsOnEvent: dataModels,
        workflow: workflow.value
      })
      await ensureNextVersion(oldVersion)
    }
  }

  const addEntity = async (entityFromLLM) => {
    let dataModelsOnWorkflow = safeStructuredClone(workflow.value.dataModels || [])

    const index = dataModelsOnWorkflow.findIndex((model) => model.name === entityFromLLM.name)

    if (index >= 0) {
      return
    }

    const dimensionTable = workflowHelpers.methods.convertLLMResponseToEntity(entityFromLLM)
    dataModelsOnWorkflow = [...dataModelsOnWorkflow, dimensionTable]

    if (dataModelsOnWorkflow) {
      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: dataModelsOnWorkflow?.length > 0 ? dataModelsOnWorkflow : undefined,
        workflow: workflow.value
      })
    }
  }

  const linkEntities = async () => {
    const entities = workflow.value.dataModels
    const chunkSize = 5
    const events = workflow.value.eventsJson

    for (let i = 0; i < events.length; i += chunkSize) {
      const chunk = events.slice(i, i + chunkSize)
      const updatedSvgGraph = safeStructuredClone(svgGraph.value)

      chunk.forEach(event => {
        updatedSvgGraph[event.id].requirementsJson = updatedSvgGraph[event.id].requirementsJson?.map(item => ({
          id: item.id,
          description: item.description,
          releaseNo: item.releaseNo,
          sortkey: item.sortkey,
          cardTypeId: item.cardTypeId,
          done: item.done,
          boundedContext: item.boundedContext,
          creatorId: item.creatorId,
          version: item.version,
          created: item.created,
          entityId: item.cardType.aggregateType
            ? entities.find(entity => entity.name === item.description)?.id
            : null,
          cardinality: item.cardinality
        }))
      })

      await workflowApi.updateWorkflow({
        svgGraph: updatedSvgGraph,
        workflow: workflow.value,
        updateNote: {
          action: 'updated root entity',
          target: ''
        }
      }, true)

      await nextTick()
    }
  }

  const wait100ms = async () => {
    return new Promise((resolve) => {
      setTimeout(() => { resolve() }, 100)
    })
  }

  const ensureNextVersion = async (oldVersion) => {
    while (oldVersion === workflow.value.version) {
      await wait100ms()
    }
  }

  const getEventFromEventObj = (obj) => {
    const cardLaneId = Object.values(svgLanes.value).find((lane) => lane.name === obj.role).id
    return workflow.value.eventsJson.find((event) => event.description === obj.description && event.laneId === cardLaneId)
  }

  const updateRouteWithEventId = async (eventId) => {
    await router.push({ path: '/workflow/' + workflow.value.projectId + '/' + workflow.value.id + '/' + eventId })
    scrollToEvent(eventId)
  }

  const splitLargeFileContent = (fileContent, maxLinesPerChunk = 1500) => {
    const lines = fileContent.split('\n')
    const chunks = []
    for (let i = 0; i < lines.length; i += maxLinesPerChunk) {
      chunks.push(lines.slice(i, i + maxLinesPerChunk).join('\n'))
    }
    return chunks
  }

  const splitAndAddLargeFile = (file, maxLineLimit = 1500) => {
    const codeChunks = []

    const contentChunks = splitLargeFileContent(file.content, maxLineLimit)
    contentChunks.forEach((chunkContent, index) => {
      codeChunks.push([{
        path: file.path,
        name: `${file.name} (Part ${index + 1}/${contentChunks.length})`,
        content: chunkContent
      }])
    })

    return codeChunks
  }

  const buildCodeChunks = (codeSnippets, chunkLineLimit = 1500) => {
    if (!codeSnippets || codeSnippets.length === 0) {
      return []
    }

    const allCodeChunks = []
    const CHUNK_LINE_LIMIT = chunkLineLimit

    let currentChunk = []
    let currentChunkLineCount = 0

    for (const file of codeSnippets) {
      if (!file.content) {
        continue
      }

      const fileLineCount = file.content.split('\n').length

      if (fileLineCount > CHUNK_LINE_LIMIT) {
        if (currentChunk.length > 0) {
          allCodeChunks.push(currentChunk)
          currentChunk = []
          currentChunkLineCount = 0
        }
        const chunks = splitAndAddLargeFile(file, CHUNK_LINE_LIMIT)

        allCodeChunks.push(...chunks)
      } else {
        if (currentChunkLineCount + fileLineCount > CHUNK_LINE_LIMIT) {
          allCodeChunks.push(currentChunk)
          currentChunk = [file]
          currentChunkLineCount = fileLineCount
        } else {
          currentChunk.push(file)
          currentChunkLineCount += fileLineCount
        }
      }
    }

    if (currentChunk.length > 0) {
      allCodeChunks.push(currentChunk)
    }

    return allCodeChunks
  }

  const buildWorkflowFromCode = async (codeSnippets, description) => {
    try {
      const allCodeChunks = buildCodeChunks(codeSnippets, 1500)

      const workflowStepsMap = new Map()
      const totalChunks = allCodeChunks.length

      for (let i = 0; i < totalChunks; i++) {
        const chunkOfSnippets = allCodeChunks[i]

        proxy.$loadingState.start(`Analyzing code... (Step ${i + 1} of ${totalChunks})`)

        const previouslyExtractedWorkflowSteps = Array.from(workflowStepsMap.values())

        const deltaResponse = await generateWorkflowFromCode({
          workflow: workflow.value,
          workflowSteps: previouslyExtractedWorkflowSteps,
          codeSnippets: chunkOfSnippets,
          description,
          stream: false
        })

        if (deltaResponse && deltaResponse.updatesAndAdditions) {
          for (const updatedStep of deltaResponse.updatesAndAdditions) {
            const stepKey = `${updatedStep.role}|${updatedStep.eventDescription}`
            workflowStepsMap.set(stepKey, updatedStep)
          }
        }
      }

      const workflowSteps = Array.from(workflowStepsMap.values())

      if (!workflowSteps?.length) {
        return
      }

      const sortResponse = await generateSortEvents({
        workflow: workflow.value,
        events: workflowSteps.map((step) => ({ role: step.role, description: step.eventDescription })),
        stream: false
      })

      const sortedEvents = sortResponse?.events

      if (!sortedEvents || !Array.isArray(sortedEvents)) {
        return workflowSteps
      }

      const sortedWorkflowSteps = []
      sortedEvents.forEach((sortedEvent) => {
        const key = `${sortedEvent.role}|${sortedEvent.description}`
        if (workflowStepsMap.has(key)) {
          sortedWorkflowSteps.push(workflowStepsMap.get(key))
        }
      })

      if (sortedWorkflowSteps.length < workflowSteps.length) {
        const sortedKeys = new Set(sortedEvents.map(e => `${e.role}|${e.description}`))
        workflowSteps.forEach(originalStep => {
          const key = `${originalStep.role}|${originalStep.eventDescription}`
          if (!sortedKeys.has(key)) {
            sortedWorkflowSteps.push(originalStep)
          }
        })
      }

      return sortedWorkflowSteps
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e)
      }
    }
  }

  const generateExampleData = async () => {
    const entities = (workflow.value.dataModels ?? []).map((entity) => ({
      name: entity.name,
      dataFields: (entity.dataFields ?? []).map((field) => ({
        name: field.name,
        primaryKey: field.primaryKey,
        exampleData: field.exampleData
      }))
    }))

    if (entities.length) {
      const exampleDataResponse = await generateEntitiesExampleData({
        aiVersion: workflow.value.aiVersion,
        entities
      })

      if (exampleDataResponse?.entities?.length > 0) {
        const entitiesCopy = safeStructuredClone(workflow.value.dataModels || [])
        const entitiesFromLLM = exampleDataResponse.entities

        const updatedEntities = entitiesCopy.map((entity, entityIndex) => {
          return {
            ...entity,
            dataFields: (entity.dataFields ?? []).map((field, fieldIndex) => ({
              ...field,
              exampleData: entitiesFromLLM[entityIndex]?.dataFields[fieldIndex]?.exampleData || field.exampleData
            }))
          }
        })

        await workflowApi.updateDataModels({
          dataModelsOnWorkflow: updatedEntities,
          workflow: workflow.value
        })
      }
    }
  }

  const createBoundedContext = async (githubInfo) => {
    const boundedContext = {
      id: randomUUID(),
      name: workflow.value.name,
      githubAccountLogin: githubInfo.githubAccountLogin || undefined,
      githubRepo: githubInfo.githubRepo || undefined,
      githubBranch: githubInfo.githubBranch || undefined,
      templateId: 'Legacy Project'
    }

    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        boundedContexts: [...(workflow.value.boundedContexts || []), boundedContext],
        updateNote: {
          action: 'added bounded context',
          target: workflow.value.name
        }
      }
    })

    const updatedEntities = (workflow.value.dataModels ?? []).map((entity) => ({
      ...entity,
      boundedContext: boundedContext.id
    }))

    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        dataModels: updatedEntities,
        updateNote: {
          action: 'updated bounded context on entity',
          target: ''
        }
      }
    })

    return boundedContext
  }

  const saveFiles = async (boundedContext, files) => {
    for (const file of files) {
      const object = {
        filepath: `root/${file.path}`,
        boundedContextId: boundedContext.id,
        prompt: '',
        contents: file.content || '',
        tags: file.tags || []
      }

      await store.dispatch('boundedContexts/createObject', object)
    }
  }

  const saveApiSpecification = async (boundedContext, swaggerDoc) => {
    const object = {
      filepath: '__spec',
      boundedContextId: boundedContext.id,
      prompt: '',
      contents: swaggerDoc,
      tags: []
    }

    await store.dispatch('boundedContexts/createObject', object)
  }

  const getTaggedFiles = async (files) => {
    const allQueries = []
    const allCommands = []
    const allEntities = (workflow.value.dataModels || []).map((entity) => ({
      id: entity.id,
      name: entity.name
    }))

    for (const event of workflow.value.eventsJson) {
      for (const card of event.requirementsJson) {
        if (card.cardType?.queryType) {
          allQueries.push({ id: card.id, name: sanitizeCardDescription(card.description || '') })
        }
        if (card.cardType?.triggerType) {
          allCommands.push({ id: card.id, name: sanitizeCardDescription(card.description || '') })
        }
      }
    }

    const workflowData = {
      commands: allCommands,
      readModels: allQueries,
      entities: allEntities
    }

    const filteredFiles = files.filter((file) => isFileRelevantForAnalysis(file.path))
    const allCodeChunks = buildCodeChunks(filteredFiles, 1500)
    const allTaggedFileResponses = []
    const totalChunks = allCodeChunks.length

    for (let i = 0; i < totalChunks; i++) {
      proxy.$loadingState.start(`Tagging code files with AI... (Step ${i + 1} of ${totalChunks})`)
      const chunkOfSnippets = allCodeChunks[i]

      const taggingResponse = await generateFileTagging({
        aiVersion: workflow.value.aiVersion,
        workflowData,
        codeSnippets: chunkOfSnippets
      })

      if (taggingResponse && taggingResponse.fileTags) {
        allTaggedFileResponses.push(...taggingResponse.fileTags)
      }
    }

    const fileTagsMap = new Map()

    for (const taggedFile of allTaggedFileResponses) {
      const path = taggedFile.path

      const tags = fileTagsMap.get(path) || new Set()
      for (const tag of taggedFile.tags) {
        tags.add(tag)
      }

      fileTagsMap.set(path, tags)
    }

    return files.map((file) => {
      const tagsSet = fileTagsMap.get(file.path)
      const fileTags = tagsSet ? Array.from(tagsSet) : []

      return { ...file, tags: fileTags }
    })
  }

  const createWorkflow = async (workflowSteps, filesData, githubInfo) => {
    try {
      let commandCardKey
      let queryCardKey
      let entityCardKey
      let gwtCardKey

      for (let i = 0; i < requirementTypes.value?.length; i++) {
        const type = requirementTypes.value[i]

        if (type.generateWithAI && (type.triggerType || type.queryType || type.aggregateType || type.gwtType)) {
          const requirementType = formatCardKey(type.title)

          if (type.triggerType) {
            commandCardKey = requirementType
          } else if (type.queryType) {
            queryCardKey = requirementType
          } else if (type.aggregateType) {
            entityCardKey = requirementType
          } else if (type.gwtType) {
            gwtCardKey = requirementType
          }
        }
      }

      for (const step of workflowSteps) {
        if (!step) {
          continue
        }

        const eventFromLLM = { description: step.eventDescription, role: step.role }
        const cardFromLLM = { ...eventFromLLM }

        if (commandCardKey && step.command?.name) {
          cardFromLLM[commandCardKey] = step.command.name
        }

        if (queryCardKey && step.queries?.length) {
          cardFromLLM[queryCardKey] = step.queries.map((query) => query.name)
        }

        if (entityCardKey && step.entities?.length) {
          const rootEntity = step.entities.find((entity) => entity.isAggregateRoot)

          if (rootEntity) {
            cardFromLLM[entityCardKey] = rootEntity.name
          }
        }

        if (gwtCardKey && step.gwt?.length) {
          cardFromLLM[gwtCardKey] = step.gwt
        }

        await addEvent(eventFromLLM)

        await addCard(cardFromLLM)

        if (step.command) {
          const writeModelFromLLM = { ...eventFromLLM, dataFields: step.command.dataFields }
          await addWriteModel(writeModelFromLLM)
        }

        if (step.queries?.length) {
          for (const query of step.queries) {
            const readModelFromLLM = { ...eventFromLLM, readModelDataFields: query.dataFields }
            await addReadModel(readModelFromLLM)
          }
        }

        if (step.entities?.length) {
          for (const entity of step.entities) {
            const entityFromLLM = { ...eventFromLLM, name: entity.name, dataFields: entity.dataFields }
            await addEntity(entityFromLLM)
          }
        }
      }

      proxy.$loadingState.start('Finalizing domain model...')
      await nextTick()
      await linkEntities()
      await generateExampleData()
      const boundedContext = await createBoundedContext(githubInfo)

      const { allFiles, filesToTag } = filesData || {}

      if (allFiles?.length && filesToTag?.length) {
        proxy.$loadingState.start('Tagging code files...')
        const filesWithTags = await getTaggedFiles(filesToTag)
        const filesToSave = allFiles.map((file) => {
          const matchingTaggedFile = filesWithTags.find((f) => f.path === file.path)
          return matchingTaggedFile || file
        })
        const swaggerDoc = convertWorkflowToSwaggerYaml(workflow.value, boundedContext)
        await saveApiSpecification(boundedContext, swaggerDoc)
        await saveFiles(boundedContext, filesToSave)
      }
    } catch (e) {
      console.log(e)
    }
  }

  return { buildWorkflowFromCode, createWorkflow }
}

export default useGenerateWorkflow
